import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, X, Check } from 'lucide-react';
import { PURCHASES, TIME_AGOS, Purchase } from '../data/purchases';
const FIRST_DELAY = 15000;
const VISIBLE_FOR = 5000;
const GAP = 15000;
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Subtle purchase notifications. First one appears after 15s, stays 5s,
 * and the next one arrives 15s later. Anchored bottom-left so it never
 * covers the primary CTAs, which sit centered on mobile.
 */
export function SalesNotifications() {
  const [current, setCurrent] = useState<{
    purchase: Purchase;
    timeAgo: string;
    id: number;
  } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (dismissed) return;
    let timeoutId: ReturnType<typeof setTimeout>;
    let idCounter = 0;
    const showNext = () => {
      idCounter += 1;
      setCurrent({
        purchase: pickRandom(PURCHASES),
        timeAgo: pickRandom(TIME_AGOS),
        id: idCounter
      });
      timeoutId = setTimeout(() => {
        setCurrent(null);
        timeoutId = setTimeout(showNext, GAP);
      }, VISIBLE_FOR);
    };
    timeoutId = setTimeout(showNext, FIRST_DELAY);
    return () => clearTimeout(timeoutId);
  }, [dismissed]);
  if (dismissed) return null;
  return <div className="fixed bottom-3 left-3 right-3 sm:right-auto sm:bottom-5 sm:left-5 z-[60] pointer-events-none" aria-live="polite" data-id="element-197" data-mp="2wiz0g8lp90xi">
      <AnimatePresence mode="wait" data-id="element-198" data-mp="30lc6ky4cz0ut">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 8
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto w-full max-w-[290px] sm:max-w-[300px] bg-navy-900 border border-gold/25 rounded-xl shadow-[0_12px_34px_rgba(8,20,38,0.4)] overflow-hidden" data-id="element-199" data-mp="2ml1ejrrbbg82">
            <div className="flex items-start gap-2.5 px-3 py-2.5" data-id="element-200" data-mp="ecmqlogw7kds">
              <span className="flex-shrink-0 mt-[1px] w-7 h-7 rounded-lg bg-clinic/12 border border-clinic/30 flex items-center justify-center" data-id="element-201" data-mp="eeziougd7b4k">
                <ShoppingBag size={13} className="text-clinic-light" strokeWidth={2.2} data-id="element-202" data-mp="1723k6pg917bl" />
              </span>

              <div className="flex-1 min-w-0" data-id="element-203" data-mp="xbil70wny0s5">
                <p className="text-[11.5px] sm:text-[12px] text-white/90 leading-snug" data-id="element-204" data-mp="xafrymbvbm92">
                  <span className="font-semibold text-white" data-id="element-205" data-mp="sc5w09ibkhme">
                    {current.purchase.name}
                  </span>{' '}
                  de{' '}
                  <span className="font-semibold text-white" data-id="element-206" data-mp="1l1363pcdn842">
                    {current.purchase.city}
                  </span>{' '}
                  accedió a Atlas Clínico
                </p>
                <p className="flex items-center gap-1 text-[9.5px] sm:text-[10px] text-gray-400 leading-snug mt-0.5" data-id="element-207" data-mp="1xbaxcmliscyh">
                  <Check size={9} className="text-clinic-light flex-shrink-0" strokeWidth={3} data-id="element-208" data-mp="229vyqpcid2oi" />
                  <span className="truncate" data-id="element-209" data-mp="2c9d0wwsjwyfq">
                    {current.purchase.country} · {current.timeAgo}
                  </span>
                </p>
              </div>

              <button onClick={() => setDismissed(true)} className="flex-shrink-0 -mr-0.5 -mt-0.5 p-1 text-gray-500 hover:text-white transition-colors duration-150 ease-out" aria-label="Cerrar notificaciones" data-id="element-210" data-mp="2g8c3wbuq2l4j">
                <X size={13} data-id="element-211" data-mp="2v15t4ozsqfme" />
              </button>
            </div>

            {/* Progress hairline */}
            <motion.div initial={{
          scaleX: 1
        }} animate={{
          scaleX: 0
        }} transition={{
          duration: VISIBLE_FOR / 1000,
          ease: 'linear'
        }} className="h-[2px] bg-gold/70 origin-left" data-id="element-212" data-mp="2zyxq3xfasrxg" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}